import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackEvent } from '../analytics';
import supabase from '../supabaseClient';
import { ZenLogo } from './ZenLogo';
import LayeredExplainer from './LayeredExplainer';
import HierarchyFlow from './HierarchyFlow';
import SportsMenu from './SportsMenu';
import { SPORTS } from './sportsData';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/* ─── Zen Assistant avatar — branded logo mark in a circular frame ─────── */
function ZenAssistantAvatar({ size = 32 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: '#060e1c',
      border: '1.5px solid rgba(0,212,255,0.45)',
      boxShadow: '0 0 8px rgba(0,212,255,0.18)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden', flexShrink: 0,
    }}>
      <img
        src="/favicon.svg"
        alt="Zen Assistant"
        style={{ width: size * 0.72, height: size * 0.72, display: 'block' }}
      />
    </div>
  );
}

/* ─── Pilot request modal ────────────────────────────────────────────── */
const PLAN_LABELS = {
  garmin: { label: 'ZenAnalytics', emoji: '🎾' },
};

function PilotModal({ product, onClose }) {
  const [name,       setName]       = useState('');
  const [email,      setEmail]      = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done,       setDone]       = useState(false);
  const [error,      setError]      = useState(null);

  const plan = PLAN_LABELS[product] || { label: product, emoji: '🎁' };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/notify/pilot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:         name.trim(),
          email:        email.trim(),
          product:      product,
          submitted_at: new Date().toISOString(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        throw new Error(data.error || `Server error ${res.status}`);
      }
      trackEvent('pilot_request', { product });
      setDone(true);
    } catch (err) {
      console.error('[PilotModal] submit error:', err);
      setError('Something went wrong. Please try again or email us directly at support@zenanalytics.io');
    } finally {
      setSubmitting(false);
    }
  };

  // Close on backdrop click
  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  // Close on Escape — the keyboard-equivalent of a backdrop click. A full-
  // screen backdrop div isn't given role="button"/tabIndex itself (that
  // would make the whole screen one giant, confusing tab stop); Escape is
  // the standard modal-dismiss pattern, and the explicit Close button
  // below already covers Tab+Enter (production readiness audit, 2026-10-21).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div onClick={handleBackdrop} style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px', fontFamily: D.font,
    }}>
      <div style={{
        background: '#0d1829', border: '1px solid #1a2d47',
        borderRadius: '18px', padding: '36px', width: '100%', maxWidth: '420px', boxSizing: 'border-box',
        position: 'relative', boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
      }}>
        {/* Close */}
        <button onClick={onClose} style={{
          position: 'absolute', top: '16px', right: '16px',
          background: 'none', border: 'none', color: '#64748b',
          fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '4px',
        }}>×</button>

        {done ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>🎁</div>
            <h3 style={{ color: '#ffffff', margin: '0 0 12px', fontSize: '20px', fontWeight: '800' }}>
              Request received!
            </h3>
            <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: 1.6, margin: '0 0 24px' }}>
              We'll review your request and send you an activation link when your pilot access is ready — usually within 24 hours.
            </p>
            <button onClick={onClose} style={{
              background: D.accent, color: '#060d1a', border: 'none',
              borderRadius: '10px', padding: '12px 28px',
              fontWeight: '700', fontSize: '14px', cursor: 'pointer', fontFamily: D.font,
            }}>Done</button>
          </div>
        ) : (
          <>
            <p style={{ color: D.accent, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 8px' }}>
              Pilot access
            </p>
            <h3 style={{ color: '#ffffff', margin: '0 0 6px', fontSize: '20px', fontWeight: '800', letterSpacing: '-0.5px' }}>
              {plan.emoji} {plan.label}
            </h3>
            <p style={{ color: '#94a3b8', fontSize: '13px', lineHeight: 1.6, margin: '0 0 24px' }}>
              Request free pilot access to <b style={{ color: '#ffffff' }}>{plan.label}</b>. We'll review your request and activate your account personally.
            </p>

            {error && (
              <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '10px 14px', color: '#fca5a5', fontSize: '13px', marginBottom: '16px' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', color: '#ffffff', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Your name</label>
                <input
                  type="text" required value={name} onChange={e => setName(e.target.value)}
                  placeholder="First and last name"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: '#0a1628', border: '1px solid #1e293b',
                    borderRadius: '8px', padding: '11px 14px',
                    color: '#ffffff', fontSize: '14px', fontFamily: D.font, outline: 'none',
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', color: '#ffffff', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Email address</label>
                <input
                  type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: '#0a1628', border: '1px solid #1e293b',
                    borderRadius: '8px', padding: '11px 14px',
                    color: '#ffffff', fontSize: '14px', fontFamily: D.font, outline: 'none',
                  }}
                />
              </div>
              <button
                type="submit" disabled={submitting}
                style={{
                  width: '100%', padding: '14px',
                  background: D.accent, color: '#060d1a',
                  border: 'none', borderRadius: '10px',
                  fontWeight: '700', fontSize: '14px', cursor: submitting ? 'not-allowed' : 'pointer',
                  fontFamily: D.font, opacity: submitting ? 0.7 : 1,
                  transition: 'opacity 0.15s',
                }}>
                {submitting ? 'Sending…' : 'Request access →'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Google Fonts (Inter) ───────────────────────────────────────────── */
if (!document.getElementById('zen-font-inter')) {
  const link = document.createElement('link');
  link.id = 'zen-font-inter';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap';
  document.head.appendChild(link);
}

/* ─── Design system ──────────────────────────────────────────────────── */
const D = {
  bg0:    '#060d1a',   // deep navy — page background
  bg1:    '#0d1829',   // card surface
  bg2:    '#111f35',   // elevated card
  bgTeal: '#061e1c',   // teal-tinted section (ocean photo: #3dbfb0)
  bgAmber:'#1a1205',   // amber-tinted section (autumn photo: #e8a030)
  border: '#1a2d47',   // subtle borders
  text:   '#ffffff',   // primary text — pure white
  muted:  '#ffffff',   // secondary text — white
  dim:    'rgba(255,255,255,0.65)',   // very muted / placeholders

  accent: '#00d4ff',   // brand cyan
  accentD:'#00b8e0',   // brand cyan — hover state
  green:  '#10b981',   // emerald-500
  purple: '#8b5cf6',   // violet-500
  teal:   '#3dbfb0',   // ocean teal (from photo)
  amber:  '#e8a030',   // autumn amber (from photo)

  font:   "'Inter', system-ui, -apple-system, sans-serif",
};

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

/* ─── Logo ───────────────────────────────────────────────────────────── */
// Moved to its own file (2026-10-21, bundle-size pass) — re-exported here
// so every existing `import { ZenLogo } from './landing/LandingPage'` keeps
// working unchanged.
export { ZenLogo } from './ZenLogo';



/* ─── Shared styles ──────────────────────────────────────────────────── */
const btnPrimary = (disabled = false) => ({
  background: disabled ? D.dim : D.accentD,
  color: disabled ? D.muted : '#ffffff',
  border: 'none',
  borderRadius: '10px',
  padding: '13px 26px',
  fontWeight: '700',
  fontSize: '15px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: D.font,
  transition: 'background 0.15s, transform 0.1s',
  letterSpacing: '-0.2px',
});

const btnGhost = {
  background: 'transparent',
  color: D.text,
  border: `1px solid ${D.border}`,
  borderRadius: '10px',
  padding: '12px 22px',
  fontWeight: '600',
  fontSize: '14px',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: D.font,
  transition: 'border-color 0.15s',
};

/* ─── Intersection observer hook ─────────────────────────────────────── */
function useVisible(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/* ─── Nav ────────────────────────────────────────────────────────────── */
export function Nav({ onSignIn, onGetStarted, onAbout, onPilotLogin, onLogoClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // "Pricing" is an in-page anchor that only exists on the landing page —
  // this Nav is shared with /sports/:slug, so navigate home first from there.
  const goToPricingFromAnywhere = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => scrollTo('pricing'), 150);
    } else {
      scrollTo('pricing');
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the mobile menu if the viewport widens back to desktop
  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 860) setMobileMenuOpen(false); };
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const closeDropdown = () => setOpenDropdown(null);

  const navItemStyle = {
    background: 'none', border: 'none',
    color: 'rgba(255,255,255,0.80)', fontSize: '14px', fontWeight: '500',
    cursor: 'pointer', padding: '8px 12px',
    fontFamily: D.font, borderRadius: '8px',
    display: 'flex', alignItems: 'center', gap: 4,
    transition: 'color 0.15s',
    whiteSpace: 'nowrap',
  };

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
      background: scrolled ? 'rgba(6,13,26,0.97)' : 'rgba(6,13,26,0.8)',
      backdropFilter: 'blur(20px)',
      borderBottom: scrolled ? `1px solid ${D.border}` : '1px solid transparent',
      transition: 'background 0.3s, border-color 0.3s',
      fontFamily: D.font,
    }} onMouseLeave={closeDropdown}>
      <div style={{
        maxWidth: '1160px', margin: '0 auto',
        padding: '0 20px', height: '64px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        {/* Left group: mobile menu toggle (Sports/Features, hidden >860px) + logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          <button
            className="nav-mobile-toggle"
            onClick={() => setMobileMenuOpen(o => !o)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            style={{
              display: 'none', background: 'none', border: 'none', cursor: 'pointer',
              color: '#ffffff', fontSize: 20, padding: '6px 8px 6px 0', lineHeight: 1,
            }}>
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
          <div
            onClick={() => {
              if (onLogoClick) { onLogoClick(); return; }
              if (location.pathname !== '/') { navigate('/'); window.scrollTo(0, 0); return; }
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <ZenLogo size={32} />
            <div>
              <span style={{ color: D.text, fontWeight: '800', fontSize: '17px', letterSpacing: '-0.5px', lineHeight: 1.1, display: 'block' }}>
                ZenAnalytics
              </span>
              <span style={{ color: D.muted, fontSize: '9px', letterSpacing: '0.04em', lineHeight: 1.3, display: 'block', maxWidth: 110 }}>
                Performance<br />analytics system
              </span>
            </div>
          </div>
        </div>

        {/* Right CTAs */}
        <div className="nav-right-ctas" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {/* Pilot login — subtle link for pilot users */}
          <button
            onClick={() => { trackEvent('cta_click', { button: 'pilot_login', source: 'nav' }); (onPilotLogin || onSignIn)?.(); }}
            className="nav-pilot-login"
            style={{
              background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.45)',
              color: '#34d399', fontSize: '13px', fontWeight: '600',
              cursor: 'pointer', padding: '8px 14px',
              borderRadius: '9px', fontFamily: D.font,
              transition: 'background 0.15s, border-color 0.15s', whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(52,211,153,0.22)'; e.currentTarget.style.borderColor = 'rgba(52,211,153,0.75)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(52,211,153,0.12)'; e.currentTarget.style.borderColor = 'rgba(52,211,153,0.45)'; }}>
            🎁 Pilot login
          </button>
          {/* Log in — returning users */}
          <button onClick={() => { trackEvent('cta_click', { button: 'login', source: 'nav' }); onSignIn?.(); }}
            className="nav-login-btn"
            style={{
              background: 'transparent', color: 'rgba(255,255,255,0.80)',
              border: `1px solid rgba(255,255,255,0.30)`,
              borderRadius: '9px', padding: '9px 16px',
              fontWeight: '500', fontSize: '14px', cursor: 'pointer',
              fontFamily: D.font, letterSpacing: '-0.2px',
              transition: 'background 0.15s, border-color 0.15s, color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)'; e.currentTarget.style.color = '#ffffff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.30)'; e.currentTarget.style.color = 'rgba(255,255,255,0.80)'; }}>
            Log in
          </button>
          {/* Choose your plan — new users */}
          <button onClick={() => { trackEvent('cta_click', { button: 'choose_plan', source: 'nav' }); goToPricingFromAnywhere(); }}
            className="nav-plan-btn"
            style={{
              background: D.accent, color: '#060d1a',
              border: 'none',
              borderRadius: '9px', padding: '9px 18px',
              fontWeight: '700', fontSize: '14px', cursor: 'pointer',
              fontFamily: D.font, letterSpacing: '-0.2px',
              transition: 'filter 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
            onMouseLeave={e => e.currentTarget.style.filter = 'brightness(1)'}>
            <span className="nav-plan-btn-full">Choose your plan →</span>
            <span className="nav-plan-btn-short">Start →</span>
          </button>
          <div className="nav-divider" style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.20)', margin: '0 4px' }} />
          <button className="nav-about-btn" onClick={() => onAbout?.()}
            style={{
              background: 'transparent', color: 'rgba(255,255,255,0.55)',
              border: 'none', padding: '9px 8px',
              fontWeight: '500', fontSize: '13px', cursor: 'pointer',
              fontFamily: D.font, letterSpacing: '-0.2px',
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#ffffff'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.55)'}>
            About
          </button>
        </div>
      </div>

      {/* Pilot login — dedicated mobile row. Heavy pilot-tester traffic right now, so
          it needs to stay one tap away rather than buried in the ☰ "More" menu. Its
          own row (not squeezed into the main bar) avoids reopening the overflow
          problem that pushed it out of the top row in the first place. */}
      <div className="nav-pilot-row" style={{
        display: 'none', justifyContent: 'center', padding: '8px 16px',
        borderTop: `1px solid ${D.border}`,
      }}>
        <button
          onClick={() => { trackEvent('cta_click', { button: 'pilot_login', source: 'nav_mobile_row' }); (onPilotLogin || onSignIn)?.(); }}
          style={{
            background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.45)',
            color: '#34d399', fontSize: '13px', fontWeight: '600',
            cursor: 'pointer', padding: '7px 16px',
            borderRadius: '9px', fontFamily: D.font, width: '100%', maxWidth: 280,
          }}>
          🎁 Pilot login
        </button>
      </div>

      {/* Mobile menu panel — only reachable via the ☰ toggle below 860px */}
      {mobileMenuOpen && (
        <div className="nav-mobile-panel" style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: 'rgba(6,13,26,0.98)', backdropFilter: 'blur(20px)',
          borderTop: `1px solid ${D.border}`, borderBottom: `1px solid ${D.border}`,
          borderBottomLeftRadius: 18, borderBottomRightRadius: 18,
          maxHeight: 'calc(100vh - 64px)', overflowY: 'auto',
          padding: '16px', zIndex: 250,
        }}>
          <button
            onClick={() => { setMobileMenuOpen(false); navigate('/'); window.scrollTo(0, 0); }}
            className="nav-m-home">
            <span className="nav-m-home-ic">⌂</span> Home
          </button>

          <div className="nav-m-label">Sports</div>
          <div className="nav-m-sports">
            {SPORTS.map(s => (
              <button key={s.slug} className="nav-m-sport"
                onClick={() => { setMobileMenuOpen(false); navigate(`/sports/${s.slug}`); window.scrollTo(0, 0); }}>
                <span className="nav-m-sport-ic">{s.emoji}</span>
                <span>{s.name}</span>
              </button>
            ))}
          </div>

          <div className="nav-m-label">More</div>
          <div className="nav-m-list">
            <button className="nav-m-row"
              onClick={() => { setMobileMenuOpen(false); trackEvent('cta_click', { button: 'choose_plan', source: 'mobile_menu' }); goToPricingFromAnywhere(); }}>
              Pricing <span className="nav-m-arrow">→</span>
            </button>
            <button className="nav-m-row"
              onClick={() => { setMobileMenuOpen(false); onAbout?.(); }}>
              About <span className="nav-m-arrow">→</span>
            </button>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 860px) {
          .nav-links-desktop { display: none !important; }
          .nav-mobile-toggle { display: flex !important; align-items: center; }
        }
        .nav-m-home { display: flex; align-items: center; gap: 10px; width: 100%; margin-bottom: 18px;
          background: color-mix(in srgb, ${D.accent} 12%, transparent); border: 1px solid color-mix(in srgb, ${D.accent} 35%, transparent);
          border-radius: 12px; padding: 11px 14px; color: #fff; font-size: 15px; font-weight: 700;
          cursor: pointer; font-family: ${D.font}; text-align: left; transition: background .15s, border-color .15s; }
        .nav-m-home:hover { background: color-mix(in srgb, ${D.accent} 20%, transparent); border-color: color-mix(in srgb, ${D.accent} 55%, transparent); }
        .nav-m-home-ic { color: ${D.accent}; font-size: 17px; line-height: 1; }
        .nav-m-label { font-size: 11px; font-weight: 700; color: rgba(255,255,255,0.35); text-transform: uppercase;
          letter-spacing: 0.1em; margin: 0 2px 10px; }
        .nav-m-sports { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 20px; }
        .nav-m-sport { display: flex; align-items: center; gap: 9px; background: rgba(255,255,255,0.04);
          border: 1px solid ${D.border}; border-radius: 10px; padding: 9px 10px;
          color: rgba(255,255,255,0.85); font-size: 13.5px; font-weight: 600; cursor: pointer;
          font-family: ${D.font}; text-align: left; transition: background .15s, border-color .15s, transform .15s; }
        .nav-m-sport:hover { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.28); transform: translateY(-1px); }
        .nav-m-sport-ic { width: 26px; height: 26px; flex: none; display: grid; place-items: center; font-size: 15px;
          border-radius: 50%; background: rgba(255,255,255,0.06); }
        .nav-m-list { display: flex; flex-direction: column; gap: 2px; }
        .nav-m-row { display: flex; align-items: center; justify-content: space-between; width: 100%;
          background: none; border: none; cursor: pointer; color: rgba(255,255,255,0.80);
          font-size: 15px; font-weight: 500; padding: 11px 2px; font-family: ${D.font};
          text-align: left; border-bottom: 1px solid rgba(255,255,255,0.06); transition: color .15s; }
        .nav-m-row:last-child { border-bottom: none; }
        .nav-m-row:hover { color: #fff; }
        .nav-m-arrow { color: ${D.accent}; opacity: .7; }
        @media (max-width: 680px) {
          /* About and the divider move into the ☰ menu's "More" section on mobile
             instead of squeezing the top bar. Pilot login also leaves the top bar
             (no room), but gets its own dedicated row below it instead of being
             buried in the menu — it's used constantly right now during testing. */
          .nav-about-btn     { display: none !important; }
          .nav-divider       { display: none !important; }
          .nav-pilot-login   { display: none !important; }
          .nav-pilot-row     { display: flex !important; }
          .nav-right-ctas    { gap: 6px !important; }
          .nav-login-btn     { padding: 6px 10px !important; font-size: 12px !important; }
          .nav-plan-btn      { padding: 6px 12px !important; font-size: 12px !important; }
        }
        .nav-plan-btn-short { display: none; }
        @media (max-width: 400px) {
          .nav-right-ctas    { gap: 5px !important; }
          .nav-login-btn     { padding: 5px 8px !important; font-size: 11px !important; }
          .nav-plan-btn      { padding: 5px 10px !important; font-size: 11px !important; }
          .nav-plan-btn-full  { display: none; }
          .nav-plan-btn-short { display: inline; }
        }
      `}</style>
    </nav>
  );
}


/* ─── Hero ───────────────────────────────────────────────────────────── */
const HERO_BG = 'https://images.unsplash.com/photo-1506016698427-b72a4aa49fcf?auto=format&fit=crop&w=1920&q=80';

function Hero({ onGetStarted }) {
  const navigate = useNavigate();
  return (
    <section style={{
      paddingTop: '120px',
      paddingBottom: '88px',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: D.font,
    }}>
      {/* Background image */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `linear-gradient(180deg, rgba(6,13,26,0.45) 0%, rgba(6,13,26,0.30) 40%, rgba(6,13,26,0.75) 100%), url(${HERO_BG})`,
        backgroundSize: 'cover', backgroundPosition: 'center 35%',
        backgroundRepeat: 'no-repeat',
        filter: 'saturate(1.3) brightness(1.1)',
        zIndex: 0,
      }} />

      {/* Ambient glow */}
      <div style={{
        position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-60%)',
        width: '700px', height: '500px',
        background: 'radial-gradient(ellipse at center, rgba(14,165,233,0.10) 0%, transparent 65%)',
        pointerEvents: 'none',
        zIndex: 1,
      }} />

      <div style={{
        maxWidth: '820px', margin: '0 auto', padding: '0 20px',
        textAlign: 'center',
        position: 'relative', zIndex: 2,
      }}>
        {/* Eyebrow */}
        <div style={{
          display: 'inline-block',
          background: 'rgba(14,165,233,0.15)',
          border: '1px solid rgba(14,165,233,0.35)',
          borderRadius: 99, padding: '5px 16px',
          color: D.accent, fontSize: 12, fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          marginBottom: 24,
        }}>
          Introducing Game Plan
        </div>

        {/* Headline */}
        <h1 style={{
          margin: '0 0 24px',
          fontSize: 'clamp(30px, 5vw, 58px)',
          fontWeight: '800',
          lineHeight: 1.1,
          color: '#ffffff',
          letterSpacing: '-2px',
        }}>
          AI performance analytics<br /><span style={{ color: D.accent, textShadow: '0 0 30px rgba(0,212,255,0.55)' }}>for racket sport players.</span>
        </h1>

        {/* Sub */}
        <p style={{
          color: 'rgba(255,255,255,0.72)',
          fontSize: 'clamp(15px, 1.8vw, 18px)',
          lineHeight: 1.7,
          margin: '0 auto 36px',
          maxWidth: '560px',
          fontWeight: '400',
        }}>
          Build your personal match records, analyse your performance, engage AI for insights — built for every level, from club players to competitive athletes who want to understand their own game.
        </p>

        {/* CTA */}
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            onClick={() => { trackEvent('cta_click', { button: 'hero_demo' }); navigate('/?demo=true'); window.scrollTo(0,0); }}
            style={{
              background: D.accent, color: '#060d1a',
              border: 'none',
              borderRadius: '10px', padding: '14px 32px',
              fontSize: '15px', fontWeight: '700', cursor: 'pointer',
              fontFamily: D.font, letterSpacing: '-0.2px',
              transition: 'filter 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
            onMouseLeave={e => e.currentTarget.style.filter = 'brightness(1)'}>
            See in demo environment →
          </button>

          <button
            onClick={() => { trackEvent('cta_click', { button: 'hero_see_plans' }); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }}
            style={{
              background: 'transparent', color: 'rgba(255,255,255,0.80)',
              border: '1px solid rgba(255,255,255,0.35)',
              borderRadius: '10px', padding: '14px 28px',
              fontSize: '15px', fontWeight: '600', cursor: 'pointer',
              fontFamily: D.font, letterSpacing: '-0.2px',
              transition: 'background 0.15s, border-color 0.15s, color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = '#ffffff'; e.currentTarget.style.color = '#ffffff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)'; e.currentTarget.style.color = 'rgba(255,255,255,0.80)'; }}>
            See plans
          </button>
        </div>
      </div>
    </section>
  );
}

/* ─── How it works ───────────────────────────────────────────────────── */
/* ─── Markdown renderer for chat ────────────────────────────────────── */
function mdToHtml(text) {
  const esc = text.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  // Split into paragraphs on double newline, process each line within
  return esc.split(/\n\n+/).map(para => {
    const lines = para.split('\n').map(line =>
      line
        .replace(/^### (.+)$/, '<strong style="font-size:13px;display:block;margin:4px 0 2px">$1</strong>')
        .replace(/^## (.+)$/,  '<strong style="font-size:14px;display:block;margin:4px 0 2px">$1</strong>')
        .replace(/^[-*] (.+)$/, '<div style="padding-left:12px;margin:2px 0">• $1</div>')
        .replace(/^\d+\. (.+)$/,(_,p)=>`<div style="padding-left:12px;margin:2px 0">${p}</div>`)
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g,    '<em>$1</em>')
    ).join('<br>');
    return `<div style="margin-bottom:8px">${lines}</div>`;
  }).join('');
}

/* ─── Getting started ─────────────────────────────────────────────────── */
/* Static fallback = everything fully visible. The one motion (line draws
 * left-to-right, numbers light in turn) only arms when JS runs, the browser
 * has IntersectionObserver, and prefers-reduced-motion is not set — it plays
 * once on first scroll into view. Step 4 is the highlighted finish line. */
const GS_MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const GETTING_STARTED_STEPS = [
  { title: 'Sign in with Google or Microsoft', desc: 'Use the account you already have. No new password.' },
  { title: 'Start your 14-day trial', desc: 'Full access from day one. Cancel anytime.' },
  { title: 'Tell us your sport and level', desc: 'Your level, goals and style give Data Coach the context to read your game.' },
  { title: 'Plan your next match', desc: 'Set a goal and tactics before you play. Rate how it went after.' },
];

function GettingStarted() {
  const ref = useRef(null);
  const [armed, setArmed] = useState(false);
  const [go, setGo] = useState(false);

  useEffect(() => {
    const mm = typeof window !== 'undefined' && window.matchMedia;
    if ((mm && mm('(prefers-reduced-motion: reduce)').matches) || !('IntersectionObserver' in window) || !ref.current) return undefined;
    setArmed(true);
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setGo(true); io.disconnect(); }
    }, { threshold: 0.35 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      aria-labelledby="zg-heading"
      className={`zg${armed ? ' zg-on' : ''}${go ? ' zg-go' : ''}`}
      style={{
        '--bg0': D.bg0, '--bg1': D.bg1, '--border': D.border, '--text': D.text,
        '--dim': D.dim, '--accent': D.accent, fontFamily: D.font,
      }}
    >
      <style>{`
        .zg { background: var(--bg0); border-bottom: 1px solid var(--border); padding: 48px 16px 52px; color: var(--text); }
        .zg *, .zg *::before, .zg *::after { box-sizing: border-box; }
        .zg-wrap { max-width: 1040px; margin: 0 auto; }
        .zg-head { text-align: center; max-width: 640px; margin: 0 auto 32px; }
        .zg-kicker { display: inline-block; font-family: ${GS_MONO}; font-size: 11px; letter-spacing: .14em; text-transform: uppercase; color: var(--accent); margin-bottom: 10px; }
        .zg-headline { margin: 0 0 10px; font-size: clamp(24px, 5.4vw, 38px); line-height: 1.12; font-weight: 800; letter-spacing: -.02em; }
        .zg-subline { margin: 0; font-size: clamp(14.5px, 3.4vw, 17px); line-height: 1.5; color: var(--dim); font-weight: 500; }

        .zg-rail { position: relative; }
        .zg-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 22px; }
        .zg-track { position: absolute; left: 21px; top: 22px; bottom: 22px; width: 2px; background: var(--border); border-radius: 2px; }
        .zg-fill { position: absolute; inset: 0; border-radius: inherit; background: var(--accent); transform-origin: top; }
        .zg-step { position: relative; display: flex; gap: 16px; align-items: flex-start; }
        .zg-num { position: relative; z-index: 1; flex: none; width: 44px; height: 44px; border-radius: 50%; display: grid; place-items: center;
          font-family: ${GS_MONO}; font-size: 14px; font-weight: 700; color: var(--accent);
          background: var(--bg1); border: 1.5px solid var(--accent); }
        .zg-step.is-final .zg-num { background: var(--accent); color: var(--bg0);
          box-shadow: 0 0 0 5px color-mix(in srgb, var(--accent) 18%, transparent), 0 8px 24px -6px color-mix(in srgb, var(--accent) 60%, transparent); }
        .zg-text { padding-top: 2px; min-width: 0; }
        .zg-title { margin: 0 0 4px; font-size: 16px; font-weight: 800; line-height: 1.3; letter-spacing: -.01em; }
        .zg-step.is-final .zg-title { color: var(--accent); }
        .zg-desc { margin: 0; font-size: 14.5px; line-height: 1.5; color: var(--dim); }

        /* motion (armed only when allowed) */
        .zg-on .zg-fill { transform: scaleY(0); transition: transform 1.4s cubic-bezier(.4,0,.2,1); }
        .zg-on.zg-go .zg-fill { transform: none; }
        .zg-on .zg-num { color: var(--dim); border-color: var(--border); background: var(--bg1); box-shadow: none;
          transition: color .35s, border-color .35s, background .35s, box-shadow .5s; transition-delay: calc(var(--i) * 420ms); }
        .zg-on.zg-go .zg-num { color: var(--accent); border-color: var(--accent); }
        .zg-on.zg-go .zg-step.is-final .zg-num { background: var(--accent); color: var(--bg0);
          box-shadow: 0 0 0 5px color-mix(in srgb, var(--accent) 18%, transparent), 0 8px 24px -6px color-mix(in srgb, var(--accent) 60%, transparent); }

        @media (min-width: 760px) {
          .zg { padding: 72px 24px 80px; }
          .zg-head { margin-bottom: 44px; }
          .zg-list { grid-template-columns: repeat(4, 1fr); gap: 20px; }
          .zg-track { left: 12.5%; right: 12.5%; top: 21px; bottom: auto; width: auto; height: 2px; }
          .zg-fill { transform-origin: left; }
          .zg-on .zg-fill { transform: scaleX(0); }
          .zg-step { flex-direction: column; align-items: center; text-align: center; gap: 14px; }
          .zg-text { padding-top: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .zg *, .zg *::before, .zg *::after { animation: none !important; transition: none !important; }
        }
      `}</style>
      <div className="zg-wrap">
        <header className="zg-head">
          <span className="zg-kicker">Getting started</span>
          <h2 id="zg-heading" className="zg-headline">From sign-in to your first Game Plan.</h2>
          <p className="zg-subline">Four steps. A few minutes.</p>
        </header>
        <div className="zg-rail">
        <span className="zg-track" aria-hidden="true"><span className="zg-fill" /></span>
        <ol className="zg-list">
          {GETTING_STARTED_STEPS.map((step, i) => (
            <li key={step.title} className={`zg-step${i === GETTING_STARTED_STEPS.length - 1 ? ' is-final' : ''}`} style={{ '--i': i }}>
              <span className="zg-num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
              <div className="zg-text">
                <h3 className="zg-title">{step.title}</h3>
                <p className="zg-desc">{step.desc}</p>
              </div>
            </li>
          ))}
        </ol>
        </div>
      </div>
    </section>
  );
}

/* ─── Pricing ────────────────────────────────────────────────────────── */
// Unsplash: racket frame & strings close-up, shallow depth of field, no logo.
const PRICING_BG = 'https://images.unsplash.com/photo-1773452549497-05f51a391451?auto=format&fit=crop&w=1920&q=80';

function Pricing({ onGetStarted, onCheckout, onPilotRequest }) {
  const [ref, visible] = useVisible();

  const plans = [
    {
      id: 'garmin', // technical plan key (Stripe price), not shown to users
      name: 'ZenAnalytics',
      icon: '🎾',
      price: '10',
      tagline: 'Your season, your matches, your data — in one place',
      topTag: '14-day free trial',
      highlight: true,
      badge: null,
      description: 'Plan every match, rate how it went, and see the facts on your game build up over a season. The insight is yours.',
      features: [
        '🎯 Season goals, leagues & tournaments — progress tracked round by round',
        '📝 Game Plans — your goal and tactics before, result and 1–5 ratings after',
        '📈 Game Analytics — form trend, game-area breakdown, Heads-Up record per opponent',
        '🧠 Data Coach — AI that reads your whole history and surfaces patterns that are easy to miss',
      ],
      cta: 'Start 14-day trial →',
      accent: D.accent,
    },
  ];

  return (
    <section id="pricing" style={{ padding: '80px 20px', fontFamily: D.font, position: 'relative', overflow: 'hidden', background: D.bg0 }}>

      {/* Background photo — racket frame & strings close-up, under the same
          kind of dark gradient overlay the Hero uses so the card stays legible. */}
      <div aria-hidden="true" style={{
        position: 'absolute', inset: 0,
        backgroundImage: `linear-gradient(180deg, rgba(6,13,26,0.92) 0%, rgba(6,13,26,0.72) 45%, rgba(6,13,26,0.94) 100%), url(${PRICING_BG})`,
        backgroundSize: 'cover', backgroundPosition: 'center 45%',
        backgroundRepeat: 'no-repeat',
        filter: 'saturate(0.85)',
        zIndex: 0,
      }} />

      {/* Soft accent glow behind the card */}
      <div aria-hidden="true" style={{
        position: 'absolute', left: '50%', top: '58%',
        width: 'min(900px, 140vw)', height: '640px', transform: 'translate(-50%, -50%)',
        background: `radial-gradient(ellipse at center, ${D.accent}1f 0%, transparent 65%)`,
        pointerEvents: 'none', zIndex: 0,
      }} />
      <div style={{ maxWidth: '1160px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
        <div style={{ textAlign: 'center', marginBottom: '52px' }}>
          <p style={{ color: D.accent, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 12px' }}>
            Pricing
          </p>
          <h2 style={{ color: D.text, fontSize: 'clamp(24px, 4vw, 38px)', fontWeight: '800', margin: 0, letterSpacing: '-0.8px' }}>
            Choose your plan.
          </h2>
        </div>

        <div ref={ref} style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '24px',
          flexWrap: 'wrap',
        }}>
          {plans.map((p, i) => (
            <div key={i}
              style={{
                background: '#000000',
                border: `1px solid ${p.accent}${p.highlight ? '99' : '55'}`,
                borderRadius: '18px', padding: '36px 32px',
                position: 'relative',
                boxShadow: p.highlight ? `0 0 60px ${p.accent}28` : `0 0 40px ${p.accent}18`,
                width: '100%', maxWidth: '460px', boxSizing: 'border-box',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(20px)',
                transition: `opacity 0.5s ease ${i * 0.12}s, transform 0.5s ease ${i * 0.12}s`,
                display: 'flex', flexDirection: 'column',
              }}
            >
              {p.badge && (
                <div style={{
                  position: 'absolute', top: '-13px', left: '50%', transform: 'translateX(-50%)',
                  background: p.accent, color: '#000',
                  borderRadius: '99px', padding: '4px 16px',
                  fontSize: '11px', fontWeight: '800', whiteSpace: 'nowrap', letterSpacing: '0.05em',
                }}>
                  {p.badge}
                </div>
              )}

              {p.topTag && (
                <div style={{
                  margin: '-36px -32px 28px -32px',
                  padding: '10px 24px',
                  background: `${p.accent}14`,
                  borderBottom: `1px solid ${p.accent}30`,
                  borderRadius: '17px 17px 0 0',
                  color: p.accent,
                  fontSize: '11px', fontWeight: '600',
                  textAlign: 'center', letterSpacing: '0.02em',
                }}>
                  {p.topTag}
                </div>
              )}

              {/* Plan icon */}
              <div style={{ marginBottom: '16px' }}>
                <span style={{ fontSize: '36px', lineHeight: 1 }}>{p.icon}</span>
              </div>

              <div style={{ color: p.accent, fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '4px' }}>
                {p.name}{p.name === 'Endurance' ? <span style={{ color: '#a0bdd0' }}>*</span> : ''}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginBottom: '4px', marginTop: '8px' }}>
                <span style={{ color: '#ffffff', fontSize: '44px', fontWeight: '900', letterSpacing: '-2px' }}>€{p.price}</span>
                <span style={{ color: '#ffffff', fontSize: '15px' }}>/mo</span>
              </div>
              <div style={{ color: '#a0bdd0', fontSize: '11px', marginBottom: '16px' }}>incl. VAT</div>
              {p.description && (
                <p style={{ color: '#a0bdd0', fontSize: '12px', lineHeight: 1.6, margin: '0 0 20px' }}>
                  {p.description}
                </p>
              )}
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                {p.features.map((f, j) => (
                  <li key={j} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <span style={{ color: p.accent, fontSize: '13px', flexShrink: 0, marginTop: '2px' }}>✓</span>
                    <span style={{ color: '#ffffff', fontSize: '13px', lineHeight: 1.5 }}>{f}</span>
                  </li>
                ))}
              </ul>

              <div style={{
                textAlign: 'center', marginBottom: '12px',
                padding: '8px 12px',
                background: 'rgba(16,185,129,0.12)',
                border: '1px solid rgba(16,185,129,0.35)',
                borderRadius: '8px',
                color: '#10b981', fontSize: '12px', fontWeight: '600',
              }}>
                14-day free trial — cancel anytime
              </div>
              <button
                onClick={() => { trackEvent('cta_click', { button: 'pricing_cta', plan: p.id }); onCheckout ? onCheckout(p.id) : onGetStarted?.(); }}
                style={{
                  width: '100%', padding: '14px',
                  background: p.highlight ? p.accent : 'transparent',
                  color: p.highlight ? '#000' : p.accent,
                  border: `1px solid ${p.accent}`,
                  borderRadius: '10px', fontWeight: '700', fontSize: '14px',
                  cursor: 'pointer', fontFamily: D.font,
                  transition: 'background 0.15s, color 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = p.accent; e.currentTarget.style.color = '#000'; }}
                onMouseLeave={e => { e.currentTarget.style.background = p.highlight ? p.accent : 'transparent'; e.currentTarget.style.color = p.highlight ? '#000' : p.accent; }}>
                {p.cta}
              </button>
              <button
                onClick={() => { trackEvent('cta_click', { button: 'pilot_request', plan: p.id }); onPilotRequest?.(p.id); }}
                style={{
                  display: 'block', width: '100%', marginTop: '10px',
                  background: 'rgba(52,211,153,0.10)', border: '1px solid rgba(52,211,153,0.40)',
                  borderRadius: '10px', padding: '11px 14px',
                  color: '#34d399', fontSize: '13px', fontWeight: '600',
                  cursor: 'pointer', fontFamily: D.font,
                  textAlign: 'center', transition: 'background 0.15s, border-color 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(52,211,153,0.20)'; e.currentTarget.style.borderColor = 'rgba(52,211,153,0.70)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(52,211,153,0.10)'; e.currentTarget.style.borderColor = 'rgba(52,211,153,0.40)'; }}>
                🎁 Request pilot access
              </button>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}

/* ─── CTA Banner ─────────────────────────────────────────────────────── */
// The page's closing call to action. On the landing page it gets the same
// handlers as the Pricing card's "Start 14-day trial" button; on the other
// pages that render it without props (About, Contact, Terms, Privacy, sport
// pages) the primary button falls back to the homepage pricing anchor. The
// secondary link opens the demo exactly like the Hero's demo button.
export function CtaBanner({ onGetStarted, onCheckout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const start = () => {
    trackEvent('cta_click', { button: 'closing_cta_trial' });
    if (onCheckout) { onCheckout('garmin'); return; }
    if (onGetStarted) { onGetStarted(); return; }
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => scrollTo('pricing'), 150);
    } else {
      scrollTo('pricing');
    }
  };
  const demo = () => {
    trackEvent('cta_click', { button: 'closing_cta_demo' });
    navigate('/?demo=true');
    window.scrollTo(0, 0);
  };
  return (
    <section className="zcta" style={{
      '--bg0': D.bg0, '--bg1': D.bg1, '--border': D.border, '--text': D.text,
      '--dim': D.dim, '--accent': D.accent,
      fontFamily: D.font,
    }}>
      <style>{`
        .zcta { position: relative; overflow: hidden; background: var(--bg0); border-top: 1px solid var(--border);
          padding: 64px 16px 72px; text-align: center; color: var(--text); }
        .zcta::before { content: ""; position: absolute; left: 50%; top: 0; width: 720px; height: 420px; transform: translate(-50%, -45%);
          background: radial-gradient(ellipse at center, color-mix(in srgb, var(--accent) 16%, transparent), transparent 68%); pointer-events: none; }
        .zcta-wrap { position: relative; max-width: 640px; margin: 0 auto; }
        .zcta-headline { margin: 0 0 10px; font-size: clamp(24px, 5.4vw, 38px); line-height: 1.12; font-weight: 800; letter-spacing: -.02em; }
        .zcta-subline { margin: 0 0 28px; font-size: clamp(14.5px, 3.4vw, 17px); line-height: 1.5; color: var(--dim); font-weight: 500; }
        .zcta-actions { display: flex; flex-direction: column; align-items: center; gap: 14px; }
        .zcta-primary { appearance: none; border: 0; cursor: pointer; font: inherit; font-size: 15px; font-weight: 700; letter-spacing: -.2px;
          background: var(--accent); color: var(--bg0); border-radius: 10px; padding: 14px 32px; transition: filter .15s; }
        .zcta-primary:hover { filter: brightness(1.1); }
        .zcta-secondary { appearance: none; border: 0; background: none; cursor: pointer; font: inherit; font-size: 14px; font-weight: 600;
          color: var(--dim); padding: 6px 4px; text-decoration: underline; text-decoration-color: color-mix(in srgb, var(--dim) 40%, transparent);
          text-underline-offset: 4px; transition: color .15s; }
        .zcta-secondary:hover { color: var(--text); }
        .zcta-primary:focus-visible, .zcta-secondary:focus-visible { outline: 2px solid var(--accent); outline-offset: 3px; }
        @media (min-width: 760px) { .zcta { padding: 88px 24px 96px; } }
        @media (prefers-reduced-motion: reduce) { .zcta * { transition: none !important; } }
      `}</style>
      <div className="zcta-wrap">
        <h2 className="zcta-headline">Your next match is the first data point.</h2>
        <p className="zcta-subline">Plan it, play it, rate it. See your game take shape over a season.</p>
        <div className="zcta-actions">
          <button type="button" className="zcta-primary" onClick={start}>Start 14-day trial →</button>
          <button type="button" className="zcta-secondary" onClick={demo}>Or look around the demo first</button>
        </div>
      </div>
    </section>
  );
}

/* ─── Footer ─────────────────────────────────────────────────────────── */
export function Footer({ onShowAbout, onShowTerms, onShowPrivacy }) {
  const navigate = useNavigate();
  const location = useLocation();

  // "Pricing" is an in-page anchor that only exists on the landing page —
  // navigate home first if the footer is rendered on another page (About,
  // Terms, Privacy, Contact all share this Footer).
  const goToPricing = () => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => scrollTo('pricing'), 150);
    } else {
      scrollTo('pricing');
    }
  };

  return (
    <footer style={{
      background: D.bg0, borderTop: `1px solid ${D.border}`,
      padding: '40px 20px', fontFamily: D.font,
    }}>
      <div style={{
        maxWidth: '1160px', margin: '0 auto',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: '16px',
      }}>
        <div
          onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <ZenLogo size={24} />
          <span style={{ color: 'rgba(255,255,255,0.72)', fontSize: '13px' }}>ZenAnalytics · Performance AI</span>
        </div>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          {[
            { label: 'Pricing', action: goToPricing },
            { label: 'About', action: onShowAbout },
            { label: 'Terms', action: onShowTerms },
            { label: 'Privacy', action: onShowPrivacy },
          ].map(l => (
            <button key={l.label} onClick={l.action} style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.72)',
              fontSize: '13px', cursor: 'pointer', fontFamily: D.font,
              padding: 0, transition: 'color 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.color = D.accent}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.72)'}>
              {l.label}
            </button>
          ))}
        </div>
        <span style={{ color: D.dim, fontSize: '12px' }}>© 2026 ZenAnalytics</span>
      </div>
    </footer>
  );
}


/* ─── Main export ────────────────────────────────────────────────────── */
/* ─── Floating Zen Assistant chat (homepage guide) ────────────────────── */
// Zen Assistant is the public site's own guide. It is NOT Data Coach —
// Data Coach is the in-app AI feature that reads a player's own history.
// Zen Assistant may describe Data Coach as a product feature, never claim to be it.
const WELCOME_MSG = { role: 'assistant', content: 'Hi, I\'m Zen Assistant — here to answer your questions about ZenAnalytics.\n\nIn short: you set your season goals, add the leagues and tournaments you play, write a Game Plan before each match and rate how it went after. Game Analytics shows the facts building up, and in the app Data Coach reads your whole history to surface the patterns.\n\nWhat would you like to know?' };
const SUGGESTED_QUESTIONS = [
  'How does a Game Plan work?',
  'What does Data Coach do in the app?',
  'Which sports are supported?',
  'What does it cost?',
];
const CHAT_SESSION_KEY = 'zc_landing_session_id';

function getOrCreateSessionId() {
  try {
    let id = localStorage.getItem(CHAT_SESSION_KEY);
    if (!id) {
      id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      });
      localStorage.setItem(CHAT_SESSION_KEY, id);
    }
    return id;
  } catch { return ''; }
}

// Greeting bubble states: 'hidden' (not yet shown) -> 'shown' (greeting visible,
// FAB glowing) -> 'dismissing' (brief "you find me here" confirmation) -> 'gone'.
const GREETING_SEEN_KEY = 'zc_greeting_seen';

function ZenAssistantChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getOrCreateSessionId());
  const bottomRef = useRef(null);

  // The FAB is always visible; the greeting bubble stays hidden until the
  // visitor reaches the Pricing section, so it never covers content higher
  // up the page. No timer fallback — if Pricing is never reached, it never
  // pops. Once dismissed it stays gone for the session.
  const [greeting, setGreeting] = useState('hidden');
  useEffect(() => {
    let seen = false;
    try { seen = sessionStorage.getItem(GREETING_SEEN_KEY) === '1'; } catch {}
    if (seen) return undefined;

    const pricing = document.getElementById('pricing');
    if (!pricing || typeof IntersectionObserver === 'undefined') return undefined;

    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) {
        setGreeting(g => (g === 'hidden' ? 'shown' : g));
        io.disconnect();
      }
    }, { rootMargin: '0px 0px -35% 0px' }); // pricing top ~2/3 up the viewport
    io.observe(pricing);
    return () => io.disconnect();
  }, []);
  const dismissGreeting = () => {
    setGreeting('dismissing');
    try { sessionStorage.setItem(GREETING_SEEN_KEY, '1'); } catch {}
    setTimeout(() => setGreeting('gone'), 2600);
  };
  const openFromGreeting = () => {
    dismissGreeting();
    setOpen(true);
  };

  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  const send = async (preset) => {
    const text = (typeof preset === 'string' ? preset : input).trim();
    if (!text || loading) return;
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/public/data-coach-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: next.slice(-10), session_id: sessionId.current }),
      });
      const data = await res.json();
      setMessages(m => [...m, { role: 'assistant', content: data.reply || 'Something went wrong, try again.' }]);
    } catch {
      setMessages(m => [...m, { role: 'assistant', content: 'Connection error — please try again.' }]);
    }
    setLoading(false);
  };

  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24,
      zIndex: 9999,
      display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12,
      fontFamily: D.font,
    }}>
      {/* Chat panel */}
      {open && (
        <div style={{
          width: 'min(420px, calc(100vw - 32px))',
          background: '#0f172a',
          border: '1.5px solid rgba(0,212,255,0.28)',
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 8px 40px rgba(0,0,0,0.55)',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px',
            background: 'rgba(0,212,255,0.06)',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <ZenAssistantAvatar size={28} />
              <div>
                <div style={{ color: '#ffffff', fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>Zen Assistant</div>
                <div style={{ color: '#00d4ff', fontSize: 10, letterSpacing: '0.04em' }}>Product questions · ZenAnalytics</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close Zen Assistant" style={{
              background: 'none', border: 'none', color: '#475569',
              fontSize: 16, cursor: 'pointer', padding: '2px 4px', lineHeight: 1,
            }}>✕</button>
          </div>

          {/* Messages */}
          <div style={{
            height: 400, overflowY: 'auto', padding: '16px 18px',
            display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '85%',
                  background: m.role === 'user' ? '#00d4ff' : 'rgba(255,255,255,0.07)',
                  color: m.role === 'user' ? '#060d1a' : '#e2e8f0',
                  borderRadius: m.role === 'user' ? '12px 12px 3px 12px' : '12px 12px 12px 3px',
                  padding: '9px 12px',
                  fontSize: 12,
                  lineHeight: 1.55,
                  fontWeight: m.role === 'user' ? 600 : 400,
                }}>
                  {m.role === 'user'
                    ? m.content
                    : <div dangerouslySetInnerHTML={{ __html: mdToHtml(m.content) }} />
                  }
                </div>
              </div>
            ))}
            {messages.length === 1 && !loading && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2 }}>
                {SUGGESTED_QUESTIONS.map(q => (
                  <button key={q} type="button" onClick={() => send(q)} style={{
                    background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.30)',
                    borderRadius: 99, padding: '6px 11px', color: '#bfefff',
                    fontSize: 11.5, cursor: 'pointer', fontFamily: 'inherit',
                  }}>{q}</button>
                ))}
              </div>
            )}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: '12px 12px 12px 3px', padding: '9px 14px', color: '#475569', fontSize: 12 }}>
                  Thinking…
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{
            display: 'flex', gap: 7, padding: '10px 14px',
            borderTop: '1px solid rgba(255,255,255,0.07)',
          }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
              placeholder="Ask about plans, features…"
              style={{
                flex: 1, background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 7, padding: '8px 11px',
                color: '#e2e8f0', fontSize: 12, outline: 'none',
                fontFamily: 'inherit',
              }}
            />
            <button onClick={() => send()} disabled={!input.trim() || loading} style={{
              background: input.trim() && !loading ? '#00d4ff' : 'rgba(255,255,255,0.06)',
              border: 'none', borderRadius: 7, padding: '8px 13px',
              color: input.trim() && !loading ? '#060d1a' : '#334155',
              fontWeight: 700, fontSize: 12, cursor: input.trim() && !loading ? 'pointer' : 'default',
              transition: 'all 0.15s', fontFamily: 'inherit', flexShrink: 0,
            }}>
              →
            </button>
          </div>
        </div>
      )}

      {/* Greeting bubble — unmissable first-visit intro, replaced by a brief
          "you find me here" confirmation on dismiss, then gone for the session. */}
      {!open && greeting === 'shown' && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          background: '#0f172a', border: '1.5px solid rgba(0,212,255,0.35)',
          borderRadius: 14, padding: '12px 14px',
          maxWidth: 260, boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
          cursor: 'pointer', animation: 'zcGreetIn 0.35s ease',
        }} className="zc-anim" onClick={openFromGreeting}>
          <ZenAssistantAvatar size={26} />
          <p style={{ color: '#e2e8f0', fontSize: 13, lineHeight: 1.5, margin: 0, flex: 1 }}>
            Hi, I'm Zen Assistant. Questions about the plan or how ZenAnalytics works? Ask me.
          </p>
          <button
            aria-label="Dismiss"
            onClick={e => { e.stopPropagation(); dismissGreeting(); }}
            style={{
              background: 'none', border: 'none', color: '#64748b',
              fontSize: 15, cursor: 'pointer', padding: 0, lineHeight: 1, flexShrink: 0,
            }}>✕</button>
        </div>
      )}
      {!open && greeting === 'dismissing' && (
        <div style={{
          background: 'rgba(15,23,42,0.92)', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 99, padding: '7px 14px',
          animation: 'zcGreetIn 0.25s ease',
        }} className="zc-anim">
          <span style={{ color: '#94a3b8', fontSize: 12 }}>If you need me, you find me here</span>
        </div>
      )}

      {/* FAB button */}
      <button
        onClick={() => { setOpen(o => !o); if (greeting !== 'gone') dismissGreeting(); }}
        style={{
          width: 52, height: 52, borderRadius: '50%',
          background: '#060e1c',
          border: '1.5px solid rgba(0,212,255,0.5)',
          boxShadow: open ? '0 4px 20px rgba(0,212,255,0.15)'
            : greeting === 'shown' ? '0 4px 28px rgba(0,212,255,0.35), 0 0 0 6px rgba(0,212,255,0.12)'
            : '0 4px 28px rgba(0,212,255,0.35)',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18,
          transition: 'all 0.2s',
          flexShrink: 0,
          animation: (!open && greeting === 'shown') ? 'zcPulse 2s ease-in-out infinite' : 'none',
        }}
        className="zc-anim"
        title="Ask Zen Assistant"
        aria-label={open ? 'Close Zen Assistant' : 'Ask Zen Assistant'}
      >
        {open ? <span style={{ color: '#475569', fontSize: 18, lineHeight: 1 }}>✕</span> : <ZenAssistantAvatar size={34} />}
      </button>

      <style>{`
        @keyframes zcPulse {
          0%, 100% { box-shadow: 0 4px 28px rgba(0,212,255,0.35), 0 0 0 6px rgba(0,212,255,0.12); }
          50%      { box-shadow: 0 4px 28px rgba(0,212,255,0.5), 0 0 0 10px rgba(0,212,255,0.05); }
        }
        @keyframes zcGreetIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .zc-anim { animation: none !important; transition: none !important; }
        }
      `}</style>
    </div>
  );
}

export default function LandingPage({ onGetStarted, onShowTerms, onShowPrivacy, onSignIn, onAbout, onCheckout, onSeeReports, onPilotLogin }) {
  const handleStart = onGetStarted || onSignIn;
  const [pilotProduct, setPilotProduct] = useState(null); // 'insights'|'endurance'|null

  return (
    <div style={{ background: D.bg0, fontFamily: D.font }}>
      {pilotProduct && (
        <PilotModal product={pilotProduct} onClose={() => setPilotProduct(null)} />
      )}
      <Nav onSignIn={onSignIn} onGetStarted={handleStart} onAbout={onAbout} onPilotLogin={onPilotLogin || onSignIn} />
      <Hero onGetStarted={handleStart} />
      <HierarchyFlow D={D} />
      <LayeredExplainer D={D} />
      <SportsMenu D={D} />
      <GettingStarted />
      <Pricing onGetStarted={handleStart} onCheckout={onCheckout} onPilotRequest={setPilotProduct} />
      <CtaBanner onGetStarted={handleStart} onCheckout={onCheckout} />
      <Footer onShowAbout={onAbout} onShowTerms={onShowTerms} onShowPrivacy={onShowPrivacy} />
      <ZenAssistantChat />
    </div>
  );
}

